import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  AppBar, Toolbar, Typography, IconButton, Box, Button, 
  Menu, MenuItem, Avatar, Divider, ListItemIcon, ListItemText,
  List, Collapse, ListItem, Drawer, useMediaQuery, useTheme
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/HomeOutlined';
import Newspaper from '@mui/icons-material/NewspaperOutlined';
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettingsOutlined';
import ModeratorPanelSettings from '@mui/icons-material/AddModeratorOutlined';
import SupervisorAccount from '@mui/icons-material/SupervisorAccountOutlined';
import Settings from '@mui/icons-material/SettingsAccessibilityOutlined';
import PowerSettingsNew from '@mui/icons-material/PowerSettingsNew';
import SensorOccupied from '@mui/icons-material/SensorOccupiedOutlined';
import Diversity2Icon from '@mui/icons-material/Diversity2';
import Contact from '@mui/icons-material/ContactPageOutlined';
import Event from '@mui/icons-material/Event';
import LocalActivity from '@mui/icons-material/LocalActivity';
import Partners from '@mui/icons-material/PeopleOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import FacebookIcon from '@mui/icons-material/Facebook';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/X';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoutIcon from '@mui/icons-material/Logout';
import TelegramIcon from '@mui/icons-material/Telegram';
import ArticleIcon from '@mui/icons-material/Article';
import CampaignIcon from '@mui/icons-material/Campaign';
import SchoolIcon from '@mui/icons-material/School';

const Navbar = ({ showModeratorBoard, showAdminBoard, currentUser, logOut }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
  const [adminBoardOpen, setAdminBoardOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const handleUserMenuOpen = (event) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const toggleAdminBoard = () => {
    setAdminBoardOpen(!adminBoardOpen);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const navigationItems = [
    { path: '/home', label: 'Home', icon: <HomeIcon /> },
    { path: '/project', label: 'Project', icon: <ArticleIcon /> },
    { path: '/contact', label: 'Contact Us', icon: <Contact /> },
    { path: '/news', label: 'News', icon: <Newspaper /> },
    { path: '/actions', label: 'Actions', icon: <LocalActivity /> },
    { path: '/events', label: 'Events', icon: <Event /> },
    { path: '/synergies', label: 'Synergies', icon: <Diversity2Icon /> },
    { path: '/awareness-material', label: 'Awareness Material', icon: <CampaignIcon /> },
    { path: '/elearning', label: 'E-Learning', icon: <SchoolIcon /> },
  ];

  const socialLinks = [
    { href: 'https://www.youtube.com/@Hector-Project', icon: <YouTubeIcon />, label: 'YouTube' },
    { href: 'https://www.linkedin.com/company/hector-isf', icon: <LinkedInIcon />, label: 'LinkedIn' },
    { href: 'https://x.com/Hector_ISF', icon: <TwitterIcon />, label: 'Twitter' },
    { href: 'https://t.me/+doGenjUBjctlNjQ0', icon: <TelegramIcon />, label: 'Telegram' },
    { 
      href: 'https://invite.viber.com/?g2=AQBrXyna0owpclMdFAZA7A%2B3ntsf0HjO6ySTb%2FEBEJab24O0d39aRvYsEvo4hQ4d', 
      icon: <img src={`${process.env.PUBLIC_URL}/images/viber.png`} alt="Viber" style={{ width: '24px', height: '24px', filter: 'brightness(0) invert(1)' }} />, 
      label: 'Viber' 
    },
  ];

  const renderUserMenu = (onClose) => (
    <>
      <MenuItem component={Link} to="/user" onClick={onClose}>
        <ListItemIcon>
          <SupervisorAccount sx={{ color: 'white' }} />
        </ListItemIcon>
        <ListItemText primary="User Board" />
      </MenuItem>
      
      {showModeratorBoard && (
        <MenuItem component={Link} to="/mod" onClick={onClose}>
          <ListItemIcon>
            <ModeratorPanelSettings sx={{ color: 'white' }} />
          </ListItemIcon>
          <ListItemText primary="Moderator Board" />
        </MenuItem>
      )}
      
      {showAdminBoard && (
        <>
          <MenuItem onClick={toggleAdminBoard}>
            <ListItemIcon>
              <AdminPanelSettings sx={{ color: 'white' }} />
            </ListItemIcon>
            <ListItemText primary="Admin Board" />
            <IconButton size="small" sx={{ ml: 'auto' }}>
              <KeyboardArrowDownIcon sx={{ color: 'white', transform: adminBoardOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />
            </IconButton>
          </MenuItem>
          
          <Collapse in={adminBoardOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <MenuItem component={Link} to="/adminNews" onClick={onClose} sx={{ pl: 4 }}>
                <ListItemIcon>
                  <Newspaper sx={{ color: 'white' }} />
                </ListItemIcon>
                <ListItemText primary="News" />
              </MenuItem>
              
              <MenuItem component={Link} to="/adminActions" onClick={onClose} sx={{ pl: 4 }}>
                <ListItemIcon>
                  <LocalActivity sx={{ color: 'white' }} />
                </ListItemIcon>
                <ListItemText primary="Actions" />
              </MenuItem>
              
              <MenuItem component={Link} to="/adminEvents" onClick={onClose} sx={{ pl: 4 }}>
                <ListItemIcon>
                  <Event sx={{ color: 'white' }} />
                </ListItemIcon>
                <ListItemText primary="Events" />
              </MenuItem>
              
              <MenuItem component={Link} to="/adminPartners" onClick={onClose} sx={{ pl: 4 }}>
                <ListItemIcon>
                  <Partners sx={{ color: 'white' }} />
                </ListItemIcon>
                <ListItemText primary="Partners" />
              </MenuItem>

              <MenuItem component={Link} to="/adminAwareness" onClick={onClose} sx={{ pl: 4 }}>
                <ListItemIcon>
                  <CampaignIcon sx={{ color: 'white' }} />
                </ListItemIcon>
                <ListItemText primary="Awareness Material" />
              </MenuItem>

              <MenuItem component={Link} to="/adminElearning" onClick={onClose} sx={{ pl: 4 }}>
                <ListItemIcon>
                  <SchoolIcon sx={{ color: 'white' }} />
                </ListItemIcon>
                <ListItemText primary="E-Learning Users" />
              </MenuItem>
            </List>
          </Collapse>
        </>
      )}
      
      <Divider sx={{ bgcolor: 'rgba(255,255,255,0.2)', my: 1 }} />
      
      <MenuItem onClick={() => { logOut(); onClose(); }}>
        <ListItemIcon>
          <LogoutIcon sx={{ color: 'white' }} />
        </ListItemIcon>
        <ListItemText primary="Logout" />
      </MenuItem>
    </>
  );

  return (
    <div>
      <AppBar position="fixed" sx={{ bgcolor: '#0651A0' }}>
        <Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: { xs: 1, sm: 2, md: 3 } }}>
          {/* Logo Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: { xs: 1, md: 0 } }}>
            <Typography 
              variant="h6" 
              component={Link} 
              to="/" 
              sx={{ 
                textDecoration: 'none', 
                color: 'inherit',
                display: 'flex',
                alignItems: 'center',
                fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' }
              }}
            >
              <img 
                src={`${process.env.PUBLIC_URL}/images/logo.png`} 
                alt="Logo" 
                style={{ 
                  width: isMobile ? '40px' : '55px', 
                  height: isMobile ? '40px' : '55px', 
                  marginRight: '8px' 
                }} 
              />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'block' } }}>
                HECTOR Project
              </Box>
            </Typography>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, justifyContent: 'center', gap: 0.5 }}>
              {navigationItems.map((item) => (
                <Button 
                  key={item.path}
                  component={Link} 
                  to={item.path} 
                  color="inherit" 
                  sx={{ 
                    textTransform: 'none',
                    fontSize: { md: '0.875rem', lg: '1rem' },
                    px: { md: 1, lg: 1.5 }
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          {/* Right Section - Social Icons & User Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
            {/* Social Icons - Hidden on very small screens */}
            {!isMobile && (
              <>
                {socialLinks.map((social, index) => (
                  <IconButton 
                    key={index}
                    color="inherit" 
                    component="a" 
                    href={social.href} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    size="small"
                    aria-label={social.label}
                  >
                    {social.icon}
                  </IconButton>
                ))}
              </>
            )}

            {/* User Menu - Desktop */}
            {!isMobile && currentUser && (
              <>
                <Button 
                  color="inherit"
                  onClick={handleUserMenuOpen}
                  endIcon={<KeyboardArrowDownIcon />}
                  sx={{ ml: 1, textTransform: 'none', fontSize: '0.875rem' }}
                >
                  <AccountCircleIcon sx={{ mr: 1 }} />
                  {currentUser.username || 'User'}
                </Button>
                <Menu
                  anchorEl={userMenuAnchorEl}
                  open={Boolean(userMenuAnchorEl)}
                  onClose={handleUserMenuClose}
                  PaperProps={{
                    sx: {
                      width: 250,
                      bgcolor: '#0651A0',
                      color: 'white',
                    }
                  }}
                >
                  {renderUserMenu(handleUserMenuClose)}
                </Menu>
              </>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="menu"
                onClick={handleMobileMenuToggle}
                sx={{ ml: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        PaperProps={{
          sx: {
            width: 280,
            bgcolor: '#0651A0',
            color: 'white',
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* User Info */}
          {currentUser && (
            <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AccountCircleIcon sx={{ mr: 1 }} />
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {currentUser.username || 'User'}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Navigation Items */}
          <List>
            {navigationItems.map((item) => (
              <ListItem 
                key={item.path}
                component={Link}
                to={item.path}
                onClick={handleMobileMenuClose}
                sx={{ 
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItem>
            ))}
          </List>

          {/* Social Links */}
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, px: 2, opacity: 0.8 }}>
              Follow Us
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, px: 2, flexWrap: 'wrap' }}>
              {socialLinks.map((social, index) => (
                <IconButton 
                  key={index}
                  color="inherit" 
                  component="a" 
                  href={social.href} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  size="small"
                  aria-label={social.label}
                  sx={{ 
                    border: '1px solid rgba(255,255,255,0.3)',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.1)'
                    }
                  }}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Box>
          </Box>

          {/* User Menu Items */}
          {currentUser && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
              <List>
                {renderUserMenu(handleMobileMenuClose)}
              </List>
            </Box>
          )}
        </Box>
      </Drawer>
    </div>
  );
};

export default Navbar;
